// src/configs/fetchInterceptor.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import axios from 'axios';
import useZustand from 'hooks/useZustand';
import dayjs from 'dayjs';
import appService from 'services/appService';
import { generateCode, getUrlFromBrowser } from 'utils/helpers';
import translateX from 'utils/translateX';
import { getClientIp } from 'utils/getClientIp';
import { toCanonicalLocale } from 'utils/localeUtils';
import app from 'configs/appResolver';
import { getQuery, getParams } from 'utils/urlUtils';
import { onProduction, shouldShowLogs } from 'utils/helpers';

import { useThemeStore, getStorageThemeItem } from 'stores/theme';
import authService from 'services/authService';
import {
  scheduleAutoLogout,
  clearAutoLogout,
  checkStoredTokenExpiration,
} from 'utils/tokenExpirationManager';
import { initMultiTabSync, setCurrentUserId } from 'utils/multiTabSync';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const unauthorizedCode = [400, 401, 403];

// *******************************************************************************
// HELPERS
// *******************************************************************************

// Normalize dayjs objects to ISO strings recursively
const normalizeDayjsObjects = data => {
  if (!data) return data;

  // Check if it's a dayjs object
  if (data && typeof data === 'object' && data.$isDayjsObject === true) {
    // Check if it has the toISOString method (real dayjs object)
    if (typeof data.toISOString === 'function') {
      return data.toISOString();
    }

    // If it's a serialized dayjs object without methods, reconstruct the date
    // Using the internal dayjs properties
    if (data.$y !== undefined && data.$M !== undefined && data.$D !== undefined) {
      try {
        // Create a new dayjs instance from the properties
        const reconstructed = dayjs()
          .year(data.$y)
          .month(data.$M)
          .date(data.$D)
          .hour(data.$H || 0)
          .minute(data.$m || 0)
          .second(data.$s || 0)
          .millisecond(data.$ms || 0);
        return reconstructed.toISOString();
      } catch (error) {
        console.warn('Failed to reconstruct dayjs object:', error);
        // Return a fallback date string
        return new Date(
          data.$y,
          data.$M,
          data.$D,
          data.$H || 0,
          data.$m || 0,
          data.$s || 0,
          data.$ms || 0
        ).toISOString();
      }
    }
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => normalizeDayjsObjects(item));
  }

  // Handle plain objects
  if (data && typeof data === 'object' && data.constructor === Object) {
    const normalized = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        normalized[key] = normalizeDayjsObjects(data[key]);
      }
    }
    return normalized;
  }

  // Return primitives as-is
  return data;
};

const getCanonLocale = fromAppConfig => {
  // Use app.LOCALE for regional formatting (static config)
  if (fromAppConfig && app.LOCALE) return toCanonicalLocale(app.LOCALE);

  // For dynamic locale, check theme store (language) first
  const locale =
    getStorageThemeItem('language') ||
    dayjs().locale() ||
    (typeof navigator !== 'undefined' && navigator?.language) ||
    app.LOCALE ||
    'en-us';

  return toCanonicalLocale(locale);
};

const getStoredToken = () => {
  try {
    return useZustand('auth', 'token', false) || null;
  } catch (err) {
    console.warn('[fetchInterceptor] Failed to get token from Zustand:', err);
    return null;
  }
};

const normalizeBearer = token => {
  if (!token) return null;
  const t = token.toString().trim();
  return t.toLowerCase().startsWith('bearer ') ? t : `Bearer ${t}`;
};

const setAuthToken = (token, axiosInstance) => {
  const normalized = normalizeBearer(token);
  if (!normalized) return;

  // Atualiza axios headers
  if (axiosInstance) axiosInstance.defaults.headers.common['Authorization'] = normalized;

  // Updates Zustand store (auto-saved to localStorage)
  try {
    useZustand('auth', 'setToken', false)(normalized.replace('Bearer ', '').trim());
  } catch (err) {
    console.warn('[fetchInterceptor] Failed to update Zustand token:', err);
  }
};

const extractAuthHeader = (headers = {}) => {
  const hAuth = headers?.authorization || headers?.Authorization;
  return hAuth ? hAuth.toString() : null;
};

const getTokenExpirationMinutes = appCfg => {
  const fromLS =
    typeof localStorage !== 'undefined' && Number(localStorage.getItem('token_exp_minutes'));
  if (Number.isFinite(fromLS) && fromLS > 0) return fromLS;

  const fromCfg = Number(appCfg?.TOKEN_EXP_MINUTES);
  if (Number.isFinite(fromCfg) && fromCfg > 0) return fromCfg;

  return 60;
};

// *******************************************************************************
// SAFE LOGIN REDIRECT (avoids redirect nesting loops)
// *******************************************************************************
function isAuthPathname(pathname) {
  const prefix = (app && app.AUTH_PREFIX_PATH) || '/auth';
  return pathname.startsWith(`${prefix}/`);
}

function gotoLoginSafely() {
  try {
    const prefix = (app && app.AUTH_PREFIX_PATH) || '/auth';
    const redirectKey = (app && app.REDIRECT_URL_KEY) || 'redirect';

    const here = window.location;
    const herePathname = here.pathname;
    const hereSearch = here.search || '';
    const herePath = `${herePathname}${hereSearch}`;

    if (isAuthPathname(herePathname)) {
      if (herePathname === `${prefix}/login`) {
        if (hereSearch) window.history.replaceState({}, '', `${prefix}/login`);
      } else {
        window.location.replace(`${prefix}/login`);
      }
      return;
    }

    const url = `${prefix}/login?${redirectKey}=${encodeURIComponent(herePath)}`;
    window.location.replace(url);
  } catch {
    window.location.replace('/auth/login');
  }
}

// *******************************************************************************
// LOGOUT FLAGS DOCUMENTATION
// *******************************************************************************
// We use different flags to track different logout scenarios:
//
// 1. SESSION_EXPIRED_AUTO (sessionStorage) - Token expired by time (scheduleAutoLogout)
//    - Set when: auto-logout after configured time (e.g., 1 hour)
//    - Shows: "Session expired" notification
//
// 2. SESSION_EXPIRED_REQUEST (sessionStorage) - Token rejected by backend (401/403)
//    - Set when: backend rejects request with expired/invalid token
//    - Shows: "Session expired" notification
//
// 3. SESSION_EXPIRED_OTHER_TAB (sessionStorage) - Forced logout by another tab
//    - Set when: user logged out in another tab (multiTabSync)
//    - Shows: "Session expired" notification (only in OTHER tabs, not the one that initiated logout)
//
// 4. EXPLICIT_LOGOUT (sessionStorage) - User manually clicked logout
//    - Set when: user clicks logout button in THIS tab
//    - Shows: Nothing (user knows they logged out)
//
// All sessionStorage flags are per-tab, so each tab handles its own notification
// *******************************************************************************

const LOGOUT_FLAGS = {
  SESSION_EXPIRED_AUTO: 'sessionExpiredAuto',
  SESSION_EXPIRED_REQUEST: 'sessionExpiredRequest',
  SESSION_EXPIRED_OTHER_TAB: 'sessionExpiredOtherTab',
  EXPLICIT_LOGOUT: 'explicitLogout',
  // Legacy flag (kept for backwards compatibility during transition)
  LOGIN_EXPIRED_LEGACY: 'loginExpired',
};

// Export flags for use in other modules
export { LOGOUT_FLAGS };

// *******************************************************************************
// AUTO LOGOUT HANDLER (chamado quando o token expira automaticamente)
// *******************************************************************************
async function handleAutoLogout() {
  try {
    // Marca como expirado por tempo (auto-logout)
    sessionStorage.setItem(LOGOUT_FLAGS.SESSION_EXPIRED_AUTO, 'true');
    // Legacy flag for backwards compatibility
    localStorage.setItem(LOGOUT_FLAGS.LOGIN_EXPIRED_LEGACY, 'true');

    // Cancel all pending requests
    cancelAllRequests();

    // Executa o logout (j redireciona internamente usando history API, sem reload)
    // Use isForced: true to indicate this is NOT a user-initiated logout
    if (authService?.signOut) {
      await authService.signOut({ replace: true, isForced: true });
    } else {
      // Fallback caso authService no esteja disponvel
      gotoLoginSafely();
    }
  } catch (error) {
    // Em caso de erro, usa fallback com reload
    gotoLoginSafely();
  }
}

// *******************************************************************************
// LAZY IMPORT OF AUTHSERVICE (avoids circular dependency)
// *******************************************************************************
// let authServiceImported = null;
//
// async function getAuthService() {
//   if (!authServiceImported) {
//     try {
//       const module = await import('services/authService');
//       authServiceImported = module.default;
//     } catch (e) {
//       console.error('[fetchInterceptor] Failed to import authService:', e);
//     }
//   }
//   return authServiceImported;
// }

// *******************************************************************************
// LAZY BOOTSTRAP (resolve baseURL e headers fixos)
// *******************************************************************************
let resolvedApp = null;
let resolvedBackendUrl = '/__pending__';
let resolvedStaticHeaders = {};
let bootstrapDone = false;

const bootstrapPromise = (async () => {
  try {
    const { default: appCfg } = await import('configs/ConfigIndex');
    resolvedApp = appCfg;

    // Build backend URL based on CONNECTION and HOMOLOGATION_MODE
    // CONNECTION: 'local' (localhost) or 'online' (vps.jarvisly.com)
    // HOMOLOGATION_MODE: true (port 8112) or false (port 8012)
    const isLocal = appCfg.CONNECTION === 'local';
    const protocol = isLocal ? 'http' : 'https';
    const host = isLocal ? appCfg.API_HOST_LOCALHOST : appCfg.API_HOST_PRODUCTION;
    const port = appCfg.HOMOLOGATION_MODE
      ? appCfg.API_PORT_HOMOLOGATION
      : appCfg.API_PORT_PRODUCTION;
    const backendUrl = `${protocol}://${host}:${port}/api`;

    const resultIp = await getClientIp();
    // const tokenExpMin = getTokenExpirationMinutes(appCfg);

    resolvedBackendUrl = backendUrl;
    resolvedStaticHeaders = {
      'Content-Type': 'application/json',
      'x-serverdomain': backendUrl,
      'x-clienturl': getUrlFromBrowser(),
      'x-application': appCfg.ID,
      'x-token': appCfg.TOKEN,
      'x-requestcode': generateCode([2, 3], 6),
      'x-resultasobject': false,
      'x-origin': 0,
      'x-summary': true,
      'x-timestamp': +new Date(),
      'x-ip': resultIp.ip || null,
      'x-accountid': null,
      'x-subscriptionid': null,
      'x-companyid': null,
      'x-timezoneoffset': dayjs().utcOffset(),
      // 'x-tokenexpirationminutes': tokenExpMin,
    };

    apiConfig.defaults.baseURL = resolvedBackendUrl;
    Object.assign(apiConfig.defaults.headers, resolvedStaticHeaders);

    const persisted = getStoredToken();
    if (persisted) {
      apiConfig.defaults.headers.common['Authorization'] = persisted;

      // Agenda auto logout baseado no token persistido
      scheduleAutoLogout(persisted, handleAutoLogout);
    }

    // Verifica se h token expirado armazenado (til aps reload)
    checkStoredTokenExpiration(handleAutoLogout);

    // Initialize multi-tab session synchronization
    initMultiTabSync({
      onForceLogout: async ({ reason, showExpiredMessage }) => {
        // Set expired flag in SESSION storage (per tab, not shared!)
        // This ensures each forced-logout tab shows the message independently
        if (showExpiredMessage) {
          sessionStorage.setItem(LOGOUT_FLAGS.SESSION_EXPIRED_OTHER_TAB, 'true');
        }

        // Cancel all pending requests
        cancelAllRequests();

        // Execute signOut with isForced=true (won't notify other tabs or set explicitLogout)
        if (authService?.signOut) {
          await authService.signOut({ replace: true, skipNavigation: false, isForced: true });
        } else {
          gotoLoginSafely();
        }
      },
    });

    // Set current user ID if we have a persisted session
    try {
      const userId = useZustand('auth', 'user', false)?._id;
      if (userId) {
        setCurrentUserId(userId);
      }
    } catch {}
  } catch {
  } finally {
    bootstrapDone = true;
  }
})();

// *******************************************************************************
// AXIOS INSTANCE
// *******************************************************************************
const apiConfig = axios.create({
  baseURL: resolvedBackendUrl,
  timeout: 120000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// abort controllers para cancelamento em massa
const controllers = new Set();

// *******************************************************************************
// REQUEST INTERCEPTOR
// *******************************************************************************
let requests = 0;

apiConfig.interceptors.request.use(
  async config => {
    if (!bootstrapDone) await bootstrapPromise;

    // bloqueia requisies quando o login expirou, exceto se x-forcerequest === true
    try {
      const loginExpiredLegacy =
        typeof localStorage !== 'undefined' &&
        localStorage.getItem(LOGOUT_FLAGS.LOGIN_EXPIRED_LEGACY) === 'true';
      const forceRequest =
        config.headers?.['x-forcerequest'] === true ||
        config.headers?.['x-forcerequest'] === 'true';
      const token = getStoredToken();

      if ((loginExpiredLegacy || !token) && !forceRequest) {
        console.warn('[fetchInterceptor] Request blocked (login expired or no token):', {
          url: config?.url,
          baseURL: config?.baseURL,
          method: config?.method,
          loginExpiredLegacy,
          hasToken: !!token,
        });

        // If blocking due to legacy loginExpired flag, also set the new sessionStorage flag
        // so SignIn.jsx can show the "session expired" notification
        if (loginExpiredLegacy) {
          sessionStorage.setItem(LOGOUT_FLAGS.SESSION_EXPIRED_AUTO, 'true');
        }

        return Promise.reject(new axios.Cancel('login-expired'));
      }
    } catch {}

    // bloqueia requisies durante logout com cancelamento nativo
    // try {
    //   if (useZustand('boot', 'isLoggingOut', false)) {
    //     return Promise.reject(new axios.Cancel('app-logout'));
    //   }
    // } catch {
    // }

    // registra AbortController para esta request
    try {
      const ctrl = new AbortController();
      config.signal = ctrl.signal;
      config.__abortCtrl = ctrl;
      controllers.add(ctrl);
    } catch {}

    if (!config.baseURL || config.baseURL === '/__pending__') {
      config.baseURL = resolvedBackendUrl;
    }

    // const tokenExpMin = getTokenExpirationMinutes(resolvedApp);

    // Check if data is FormData (file uploads)
    const isFormData = config.data instanceof FormData;

    config.headers = {
      ...resolvedStaticHeaders,
      ...config.headers,
      'x-timestamp': +new Date(),
      'x-requestcode': generateCode([2, 3], 6),
      'x-clienturl': getUrlFromBrowser(),
      // 'x-tokenexpirationminutes': tokenExpMin,
    };

    // For FormData, remove Content-Type so browser can set it with boundary
    if (isFormData) {
      delete config.headers['Content-Type'];
    }

    const jwtToken = getStoredToken();
    if (jwtToken) config.headers['authorization'] = jwtToken;

    // Normalize dayjs objects in request data for POST, PUT, PATCH
    // Skip FormData (file uploads) - it cannot be processed
    if (
      config.data &&
      !isFormData &&
      ['post', 'put', 'patch'].includes(config.method?.toLowerCase())
    ) {
      config.data = normalizeDayjsObjects(config.data);
    }

    // 
    // REQUEST DECORATOR METADATA (already executed in axiosService)
    // Just preserve metadata from payload for console logging
    // 
    // Metadata comes from axiosService when decorator was executed
    const requestDecoratorExecuted = config._decoratorExecuted || false;
    const requestDataRaw = config._dataRaw || null;
    const requestDataDecorated = config._dataDecorated || null;

    // Store for console logging (keep same property names for compatibility)
    config._requestDecoratorExecuted = requestDecoratorExecuted;
    config._requestDataRaw = requestDataRaw;
    config._requestDataDecorated = requestDataDecorated;

    requests++;
    useThemeStore.getState().setWorking(requests);
    try {
      useZustand('boot', 'incRequests', false)();
    } catch {}

    appService.notificationDestroy('try-login');

    return config;
  },
  error => {
    requests--;
    useThemeStore.getState().setWorking(requests);
    try {
      useZustand('boot', 'decRequests', false)(false);
      useZustand('boot', 'tryFinishFirstBootSoon', false)();
    } catch {}

    appService.notificationDestroy('try-login');

    return Promise.reject(error);
  }
);

// *******************************************************************************
// RESPONSE INTERCEPTOR
// *******************************************************************************
apiConfig.interceptors.response.use(
  response => {
    const docs = response?.data;
    if (!onProduction() && docs?.password) {
      delete docs.password;
    }

    requests--;
    useThemeStore.getState().setWorking(requests);
    try {
      useZustand('boot', 'decRequests', false)(true);
      useZustand('boot', 'tryFinishFirstBootSoon', false)();
    } catch {}

    // Captura e atualiza o token se vier na resposta
    try {
      const newToken = extractAuthHeader(response.headers);
      if (newToken) {
        setAuthToken(newToken, apiConfig);

        // Re-agenda auto logout com o novo token
        scheduleAutoLogout(newToken, handleAutoLogout);

        if (shouldShowLogs()) {
        }
      }
    } catch (err) {
      console.warn('[fetchInterceptor] Failed to update token from response:', err);
    }

    const result = response?.data;
    const requestCode = response.config.headers['x-requestcode'];
    const timestamp = Number(response.config.headers['x-timestamp']);
    const totalTime = +new Date() - timestamp;

    // appService.console(
    //   's',
    //   `${requestCode} ${response.config.method.toUpperCase()} ${response.config.url} ${totalTime}ms`,
    //   result
    // );

    // 
    // EXECUTE RESPONSE DECORATORS (if configured in module)
    // Only applies when the request URL matches the page's API URL
    // This prevents decorators from running on auxiliary requests (e.g., /ia/prices)
    //
    // Supported decorators:
    // - responseDoc: (doc) => doc  - Runs for EACH document (N times for N docs)
    // - responseData: (data) => data - Runs ONCE with the entire array/object
    // 
    let transformedData = null;
    let decoratorExecuted = false;
    let responseDataRaw = null;

    try {
      const currentPage = useZustand('module', 'currentPage', false);
      const responseDocFn = currentPage?.decorators?.responseDoc;
      const responseDataFn = currentPage?.decorators?.responseData;
      const pageApiUrl = currentPage?.api?.url;

      // Only apply decorators if:
      // 1. At least one decorator function exists
      // 2. Page has an API URL configured
      // 3. Request URL starts with (or matches) the page's API URL
      const requestUrl = response.config.url?.split('?')[0] || '';
      const apiUrlBase = pageApiUrl?.replace(/:\w+\??/g, '').replace(/\/+$/, '') || '';
      const hasDecorators =
        typeof responseDocFn === 'function' || typeof responseDataFn === 'function';
      const shouldApplyDecorator = hasDecorators && pageApiUrl && requestUrl.startsWith(apiUrlBase);

      if (shouldApplyDecorator) {
        // Save RAW data BEFORE transformation
        responseDataRaw = result?.data;

        const isArray = Array.isArray(result?.data);
        let processedData = result?.data;

        // 1. Apply responseDoc decorator (runs for each document)
        if (typeof responseDocFn === 'function') {
          const payload = isArray ? processedData : [processedData];
          transformedData = payload.map(doc => responseDocFn(doc)) || null;
          processedData = isArray ? transformedData : transformedData?.[0];
        }

        // 2. Apply responseData decorator (runs once with entire data)
        if (typeof responseDataFn === 'function') {
          processedData = responseDataFn(processedData) || processedData;
        }

        // Replace result.data with transformed data
        result.data = processedData || null;
        decoratorExecuted = true;
      }
    } catch (decoratorError) {
      console.warn('[fetchInterceptor] Response decorator failed:', decoratorError);
      // Continue with original data if decorator fails
    }

    // Detailed log group (similar to apiTracker) - DEVELOPMENT/HOMOLOGATION ONLY
    if (shouldShowLogs()) {
      // const groupTitle = `${dayjs().format('HH:mm:ss.SSS')}  ${requestCode} ${response.config.method.toUpperCase()} ${response.config.url} ${totalTime}ms`;
      const groupTitle = `${dayjs().format('HH:mm:ss.SSS')}  ${requestCode} ${response.config.method.toUpperCase()} ${response.config.url} ${totalTime}ms |  REQUEST`;
      console.groupCollapsed('\x1b[32m%s\x1b[0m', groupTitle);

      // Extract params by comparing URL with schema from currentPage config
      const currentPage = useZustand('module', 'currentPage', false);
      const schemaUrl = currentPage?.api?.url || '';
      const urlWithoutQuery = response.config.url?.split('?')[0] || '';

      const urlParams = schemaUrl ? getParams(urlWithoutQuery, schemaUrl) : {};
      console.log('[fetchInterceptor] Params:', urlParams);

      // Parse query from URL (handles relative URLs)
      const urlQueryString = response.config.url?.split('?')[1] || '';
      const urlQueryParams = Object.fromEntries(new URLSearchParams(urlQueryString));
      console.log(' Query:', urlQueryParams);
      console.log(' Method:', response.config.method.toUpperCase());
      console.log(' Headers (default):', apiConfig?.defaults?.headers);
      console.log(' Headers (request):', response.config.headers);

      // Show RAW data (before decorator) if request decorator was executed
      if (response.config._requestDecoratorExecuted && response.config._requestDataRaw) {
        console.log(
          '%c Request RAW Data (Object):',
          'color: #ffa94d; font-weight: bold;',
          response.config._requestDataRaw
        );
        console.groupCollapsed('%c RAW (JSON - Copy):', 'color: #ffa94d; font-weight: bold;');
        console.log(JSON.stringify(response.config._requestDataRaw, null, 2));
        console.groupEnd();
      }

      // Show DECORATED data (after transformation) if request decorator was executed
      if (response.config._requestDecoratorExecuted && response.config._requestDataDecorated) {
        console.log(
          '%c Request DECORATED Data (Object):',
          'color: #748ffc; font-weight: bold;',
          response.config._requestDataDecorated
        );
        console.groupCollapsed(
          '%c DECORATED (JSON - Copy):',
          'color: #748ffc; font-weight: bold;'
        );
        console.log(JSON.stringify(response.config._requestDataDecorated, null, 2));
        console.groupEnd();
      }

      // Show regular Body if no decorator was executed
      // Skip FormData (file uploads) - cannot be JSON parsed
      const isResponseFormData = response.config.data instanceof FormData;
      if (
        !response.config._requestDecoratorExecuted &&
        response.config.data &&
        !isResponseFormData
      ) {
        try {
          console.log(' Body (Object):', JSON.parse(response.config.data));

          console.groupCollapsed(
            '%c Body (JSON - Copy to Postman):',
            'color: #51cf66; font-weight: bold;'
          );
          console.log(JSON.stringify(JSON.parse(response.config.data), null, 2));
          console.groupEnd();
        } catch (e) {
          console.warn(' No foi possvel stringificar o body:', e);
        }
      }

      // Stack trace para rastrear origem da chamada
      // console.groupCollapsed('%c Stack', 'font-weight: normal');
      // console.trace();
      // console.groupEnd();

      console.groupEnd();

      // Response log group (server response)
      // Internal requests (via axiosService): always light green for RESPONSE
      // The success/error of the business logic is not relevant for log color
      // HTTP errors (4xx, 5xx) are handled in the error interceptor and shown in red
      const responseTitle = `${dayjs().format('HH:mm:ss.SSS')}  ${requestCode} ${response.config.method.toUpperCase()} ${response.config.url} ${totalTime}ms |  RESPONSE`;
      const responseColor = '\x1b[92m%s\x1b[0m'; // light green for internal requests
      console.groupCollapsed(responseColor, responseTitle);
      console.log(' Status:', response.status, response.statusText);

      // Server info
      const serverVersion = result?.summary?.serverInfo?.build?.version;
      if (serverVersion) {
        console.log(' Server:', `v${serverVersion}`);
      }

      // Metrics
      const elapsedTime = result?.summary?.elapsedTime?.full;
      const dbIO = result?.summary?.frequency?.databaseProcess;
      const extReqs = result?.summary?.frequency?.externalRequests;
      if (elapsedTime !== undefined || dbIO !== undefined || extReqs !== undefined) {
        const metrics = [];
        if (elapsedTime !== undefined) metrics.push(`Time: ${elapsedTime}ms`);
        if (dbIO !== undefined) metrics.push(`Database I/O: ${dbIO}`);
        if (extReqs !== undefined) metrics.push(`External Requests: ${extReqs}`);
        console.log(' Metric:', metrics.join(' | '));
      }

      console.log(' Response Headers:', response.headers);
      console.log(' Response Data (Object):', result);

      // Show RAW data (before decorator)
      if (decoratorExecuted && responseDataRaw) {
        console.log(
          '%c Response RAW Data (Object):',
          'color: #ffa94d; font-weight: bold;',
          responseDataRaw
        );

        try {
          console.groupCollapsed('%c RAW (JSON - Copy):', 'color: #ffa94d; font-weight: bold;');
          console.log(JSON.stringify(responseDataRaw, null, 2));
          console.groupEnd();
        } catch (e) {
          console.warn(' No foi possvel stringificar os dados RAW:', e);
        }
      } else {
        // No decorator, show normal response
        try {
          // console.groupCollapsed('%c RAW (JSON - Copy):', 'color: #ffa94d; font-weight: bold;');
          console.log(JSON.stringify(result?.data, null, 2));
          // console.groupEnd();

        } catch (e) {
          console.warn(' No foi possvel stringificar a resposta:', e);
        }
      }

      // Show DECORATED data (after transformation)
      if (decoratorExecuted && transformedData) {
        console.log(
          '%c Response DECORATED Data (Object):',
          'color: #748ffc; font-weight: bold;',
          result?.data
        );

        try {
          console.groupCollapsed(
            '%c DECORATED (JSON - Copy):',
            'color: #748ffc; font-weight: bold;'
          );

          console.log(JSON.stringify(result?.data, null, 2));

          console.groupEnd();
        } catch (e) {
          console.warn(' No foi possvel stringificar os dados decorados:', e);
        }
      }

      // Stack trace para rastrear origem da chamada
      // console.groupCollapsed('%c Stack', 'font-weight: normal');
      // console.trace();
      // console.groupEnd();

      console.groupEnd();
    }

    return result;
  },
  async error => {
    // console.error(' FETCH INTERCEPTOR ERROR:', {
    //   status: error?.response?.status,
    //   url: error?.config?.url,
    //   method: error?.config?.method,
    //   message: error?.message,
    //   code: error?.code,
    // });

    // cleanup controller
    try {
      if (error?.config?.__abortCtrl) controllers.delete(error.config.__abortCtrl);
    } catch {}

    // silncio para cancelamentos
    if (
      axios.isCancel(error) ||
      error?.code === 'ERR_CANCELED' ||
      error?.message === 'app-logout' ||
      error?.message === 'login-expired'
    ) {
      requests = Math.max(0, requests - 1);
      useThemeStore.getState().setWorking(requests);
      try {
        useZustand('boot', 'decRequests', false)(true);
        useZustand('boot', 'tryFinishFirstBootSoon', false)();
      } catch {}
      return Promise.reject(error);
    }

    requests--;
    useThemeStore.getState().setWorking(requests);
    try {
      useZustand('boot', 'decRequests', false)(false);
      useZustand('boot', 'tryFinishFirstBootSoon', false)();
    } catch {}

    let alreadyShowedError = false;
    const notificationParam = {
      key: 'try-login',
      title: '',
      description: `${error?.config?.method?.toUpperCase?.()}: `,
    };

    if (!error?.config) {
      notificationParam.title = `500: ${translateX('server_not_found')}`;
      notificationParam.description = resolvedBackendUrl;
      notificationParam.duration = 0;

      // try {
      //   const authService = await getAuthService();
      //   if (authService?.signOut) await authService.signOut({ replace: true });
      // } catch {}
      appService.notification({ flag: 'e', ...notificationParam });
      // return Promise.reject(error);
    }

    const requestCode = error?.config?.headers?.['x-requestcode'];
    const timestamp = Number(error?.config?.headers?.['x-timestamp']);
    const totalTime = +new Date() - timestamp;
    const url = (error?.config?.baseURL || '').replace('/api', '');
    const isInternalRequest =
      error?.config?.url?.substring(0, 2) === '/v' || error?.config?.url?.substring(0, 1) === 'v';

    // appService.console(
    //   'e',
    //   `${requestCode} ${error?.config?.method?.toUpperCase?.()} ${error?.config?.url} ${totalTime}ms`,
    //   error?.response?.data
    // );

    // Error response log group (red for errors) - DEVELOPMENT/HOMOLOGATION ONLY
    if (shouldShowLogs()) {
      // const responseTitle = `${dayjs().format('HH:mm:ss.SSS')}  ${requestCode} ${response.config.method.toUpperCase()} ${response.config.url} ${totalTime}ms |  RESPONSE`;
      const errorResponseTitle = `${dayjs().format('HH:mm:ss.SSS')}  ${error?.config?.method?.toUpperCase?.()} ${error?.config?.url} ${totalTime}ms | ERROR `;
      console.groupCollapsed('\x1b[31m%s\x1b[0m', errorResponseTitle);
      console.error(
        ' Status:',
        error?.response?.status,
        error?.response?.statusText || error?.message
      );
      console.error(' URL:', error?.config?.url);

      // Extract params by comparing URL with schema from currentPage config
      const errorCurrentPage = useZustand('module', 'currentPage', false);
      const errorApiSchemaUrl = errorCurrentPage?.api?.url || '';
      const errorRoutingEndpoint = errorCurrentPage?.routing?.endpoint || '';
      const errorSchemaUrl = errorApiSchemaUrl + errorRoutingEndpoint;
      const errorUrlWithoutQuery = error?.config?.url?.split('?')[0] || '';
      const errorUrlParams = errorSchemaUrl ? getParams(errorUrlWithoutQuery, errorSchemaUrl) : {};
      console.error(' Params:', errorUrlParams);

      // Parse query from URL (handles relative URLs)
      const errorUrlQueryString = error?.config?.url?.split('?')[1] || '';
      const errorUrlQueryParams = Object.fromEntries(new URLSearchParams(errorUrlQueryString));
      console.error(' Query:', errorUrlQueryParams);
      console.error(' Error Data (Object):', error?.response?.data);
      console.error(
        ' Request Payload:',
        error?.config?.data ? JSON.parse(error?.config?.data) : null
      );
      // console.error(' Request Method:', error?.config?.method?.toUpperCase?.());
      // console.error(' Request URL:', error?.config?.url);
      console.error(' Total Time:', `${totalTime}ms`);

      try {
        console.log('%c Reques Payload (JSON - Copy):', 'color: #ff6b6b; font-weight: bold;');
        console.error(
          error?.config?.data ? JSON.stringify(JSON.parse(error?.config?.data), null, 2) : null
        );
        console.log('%c Error Response (JSON - Copy):', 'color: #ff6b6b; font-weight: bold;');
        console.log(JSON.stringify(error?.response?.data, null, 2));
      } catch (e) {
        console.warn(' No foi possvel stringificar o erro:', e);
      }

      // Stack trace para rastrear origem do erro
      console.groupCollapsed('%c Stack', 'font-weight: normal');
      console.trace();
      console.groupEnd();
      console.groupEnd();
    }

    // se est fazendo logout, no force redirecionamentos nem notificaes
    try {
      if (useZustand('boot', 'isLoggingOut', false)) {
        return Promise.reject(error);
      }
    } catch {}

    if (
      !alreadyShowedError &&
      unauthorizedCode.includes(error?.response?.status) &&
      isInternalRequest
    ) {
      // console.error(' INTERCEPTOR UNAUTHORIZED:', {
      //   status: error?.response?.status,
      //   url: error?.config?.url,
      //   method: error?.config?.method,
      //   message: error?.response?.data?.message,
      //   data: error?.response?.data,
      // });

      // Extract message from nested data structure (backend sends in data.data.message)
      const msg = error?.response?.data?.data?.message || error?.response?.data?.message || '';
      const responseData = error?.response?.data || {};

      // Check if it's a JWT/token expiration issue (real authentication problem)
      const isTokenExpired =
        msg?.toLowerCase?.().includes?.('expired') ||
        msg?.toLowerCase?.().includes?.('jwt') ||
        msg?.toLowerCase?.().includes?.('token');

      // Handle 400 errors (bad request) - usually business logic errors
      if (error?.response?.status === 400) {
        // Document not found case
        if (
          msg?.toLowerCase?.().includes?.('document not returned') ||
          msg?.toLowerCase?.().includes?.('not found') ||
          msg?.toLowerCase?.().includes?.('failed to update')
        ) {
          const idMatch = msg.match(/id:\s*([a-f0-9]{24})/i);
          const docId = idMatch ? idMatch[1] : 'unknown';

          // appService.notification({
          //   flag: 'error',
          //   title: translateX('document_not_found'),
          //   description: translateX('document_not_found_description', ['%ID%'], [docId]),
          //   duration: 5,
          // });

          alreadyShowedError = true;
          return Promise.reject(error);
        }

        // If forceLogout is explicitly true, then logout
        if (responseData?.forceLogout === true) {
          // Mark as expired by request (backend rejected token)
          sessionStorage.setItem(LOGOUT_FLAGS.SESSION_EXPIRED_REQUEST, 'true');
          localStorage.setItem(LOGOUT_FLAGS.LOGIN_EXPIRED_LEGACY, 'true');
          await authService?.signOut?.({ replace: true, isForced: true });
          gotoLoginSafely();
          return Promise.reject(error);
        }

        // Skip generic notification for structured business errors (with errorKey)
        // These errors are handled by the calling component (e.g., emission errors)
        // Check both root level and nested data (backend may nest at data.data)
        const nestedData = responseData?.data || {};
        if (
          responseData?.errorKey ||
          responseData?.errorCode ||
          responseData?.isEmissionError ||
          nestedData?.errorKey ||
          nestedData?.errorCode ||
          nestedData?.isEmissionError
        ) {
          alreadyShowedError = true;
          return Promise.reject(error);
        }

        // Otherwise, just show the error without logging out
        appService.notification({
          flag: 'error',
          title: translateX('request_failed'),
          description: msg || translateX('bad_request'),
          duration: 5,
        });

        alreadyShowedError = true;
        return Promise.reject(error);
      }

      // Handle 401/403 - these HTTP status codes indicate authentication failure
      // 401 Unauthorized: token missing or invalid
      // 403 Forbidden: token expired or insufficient permissions (usually expired)
      if (
        isTokenExpired ||
        error?.response?.status === 401 ||
        error?.response?.status === 403 ||
        responseData?.forceLogout === true
      ) {
        const token = getStoredToken();

        if (token) {
          // Mark as expired by request (backend rejected token)
          sessionStorage.setItem(LOGOUT_FLAGS.SESSION_EXPIRED_REQUEST, 'true');
          localStorage.setItem(LOGOUT_FLAGS.LOGIN_EXPIRED_LEGACY, 'true');
          await authService?.signOut?.({ replace: true, isForced: true });
          gotoLoginSafely();
          return Promise.reject(error);
        }
      }

      if (notificationParam.title) {
        alreadyShowedError = true;
        appService.notificationDestroy('try-login');
        appService.notification({ flag: 'w', ...notificationParam });
      }

      return Promise.reject(error);
    }

    if (!alreadyShowedError && error?.response?.status === 404) {
      notificationParam.title = `404: ${translateX('api_route_not_found')}`;
      notificationParam.description += error?.response?.config?.url;
      notificationParam.duration = 0;

      if (error?.response?.config?.url?.includes?.('get-account-by-token')) {
        try {
          const authService = await getAuthService();
          if (authService?.signOut) await authService.signOut({ replace: true });
        } catch {}
      }

      alreadyShowedError = true;
      appService.notificationDestroy('try-login');
      appService.notification({ flag: 'e', ...notificationParam });
    }

    if (!alreadyShowedError && error?.response?.status === 500) {
      // Extract message from nested data structure (backend sends in data.data.message)
      const msg = error?.response?.data?.data?.message || error?.response?.data?.message || '';

      // Handle invalid MongoDB ObjectId
      if (msg?.toLowerCase?.().includes?.('not a valid mongo object id')) {
        const idMatch = msg.match(/\(([^)]+)\)/);
        const invalidId = idMatch ? idMatch[1] : 'undefined';

        // appService.notification({
        //   flag: 'error',
        //   title: translateX('invalid_id'),
        //   description: translateX('invalid_mongo_id_description', ['%ID%'], [invalidId]),
        //   duration: 5,
        // });

        alreadyShowedError = true;
        return Promise.reject(error);
      }

      // Handle authentication errors (account not found, etc.) - let authService handle these
      if (
        msg?.toLowerCase?.().includes?.('account not found') ||
        msg?.toLowerCase?.().includes?.('invalid credentials') ||
        msg?.toLowerCase?.().includes?.('authentication') ||
        error?.config?.url?.includes?.('/signin') ||
        error?.config?.url?.includes?.('/signup')
      ) {
        alreadyShowedError = true;
        return Promise.reject(error);
      }

      // Generic 500 error
      notificationParam.title = `500: ${translateX('internal_server_error')}`;
      notificationParam.description += error?.response?.config?.url;
      notificationParam.duration = 0;

      alreadyShowedError = true;
      appService.notificationDestroy('try-login');
      appService.notification({ flag: 'e', ...notificationParam });
    }

    // Handle timeout errors (ECONNABORTED)
    if (
      !alreadyShowedError &&
      (error?.code === 'ECONNABORTED' || error?.message?.toLowerCase?.().includes?.('timeout'))
    ) {
      notificationParam.title = translateX('request_timeout');
      notificationParam.description = `${error?.config?.method?.toUpperCase?.() || 'REQUEST'}: ${error?.config?.url || ''}`;
      notificationParam.duration = 8;

      alreadyShowedError = true;
      appService.notificationDestroy('try-login');
      appService.notification({ flag: 'w', ...notificationParam });
    }

    if (!alreadyShowedError && error?.message === 'Network Error') {
      if (isInternalRequest) {
        notificationParam.title = `500: ${translateX('server_not_found')}`;
        notificationParam.description = url;

        try {
          const authService = await getAuthService();
          if (authService?.signOut) await authService.signOut({ replace: true });
        } catch {}
      } else {
        notificationParam.title = `500: ${translateX('url_not_found')}`;
        notificationParam.description = error?.config?.url;
      }

      notificationParam.duration = 0;
      alreadyShowedError = true;
      appService.notificationDestroy('try-login');
      appService.notification({ flag: 'e', ...notificationParam });
    }

    // Generic error handler - only show if we have valid status
    if (!alreadyShowedError && error?.response?.status && error?.response?.status !== 400) {
      notificationParam.title = `${error.response.status}: ${translateX('internal_server_error')}`;
      notificationParam.description += error?.response?.config?.url || error?.config?.url || '';
      notificationParam.duration = 0;
      appService.notificationDestroy('try-login');
      appService.notification({ flag: 'e', ...notificationParam });
    }

    return Promise.reject(error);
  }
);

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default apiConfig;

export function cancelAllRequests() {
  for (const c of controllers) {
    try {
      c.abort();
    } catch {}
  }
  controllers.clear();
}

export function clearAuthHeader() {
  try {
    delete apiConfig.defaults.headers.common.Authorization;

    apiConfig.defaults.headers['x-accountid'] = null;
    apiConfig.defaults.headers['x-subscriptionid'] = null;
    apiConfig.defaults.headers['x-companyid'] = null;

    clearAutoLogout();
  } catch {}
}

// *******************************************************************************
// HELPERS
// *******************************************************************************
